import { create } from 'zustand';
import { toast } from '@insight-health-ai/roseate/components/toast';
import { logger } from 'src/services';
import { Form } from 'src/models';
import {
  getWebFormNextApi,
  submitWebFormApi,
  WebFormSubmitPayload,
} from 'src/api/form';

type State = {
  loading: boolean;
  submitting: boolean;
  activeForm: Form | null;
  prefillData: Record<string, unknown>;
  isComplete: boolean;
  error: string | null;
};

type Actions = {
  fetchNextForm: () => Promise<void>;
  submitForm: (payload: WebFormSubmitPayload) => Promise<boolean>;
  reset: () => void;
};

const initialState: State = {
  loading: false,
  submitting: false,
  activeForm: null,
  prefillData: {},
  isComplete: false,
  error: null,
};

export const useWebFormStore = create<State & Actions>((set) => ({
  ...initialState,
  fetchNextForm: async () => {
    set({ loading: true, error: null });
    try {
      const { data } = await getWebFormNextApi();
      set({
        activeForm: data.next_form || null,
        prefillData: data.prefill_data || {},
        isComplete: !data.next_form,
      });
    } catch (error) {
      const message = 'Failed to load form';
      logger.error(message, {}, error as Error);
      set({ error: message });
    } finally {
      set({ loading: false });
    }
  },
  submitForm: async (payload) => {
    set({ submitting: true, error: null });
    try {
      const { data } = await submitWebFormApi(payload);
      if (data.is_complete) {
        set({ activeForm: null, isComplete: true });
      } else if (data.next_form) {
        set({ activeForm: data.next_form });
      }
      return true;
    } catch (error) {
      const message = 'Failed to submit form';
      toast.error(message);
      logger.error(message, {}, error as Error);
      set({ error: message });
      return false;
    } finally {
      set({ submitting: false });
    }
  },
  reset: () => set(initialState),
}));
